import React, { useState, useEffect } from 'react';
import {
  ShieldCheck, Download, Copy, Check, FileCode2, Loader2,
  AlertTriangle, Terminal, ListOrdered
} from 'lucide-react';

const REF_PATH = '/reference/Iso20022BahMessageSigner.java';
const VERIFIER_PATH = '/reference/Iso20022BahMessageVerifier.java';

const PROFILE = [
  { aspect: 'Canonicalization', value: 'Exclusive C14N (SignedInfo and every reference transform)', uri: 'http://www.w3.org/2001/10/xml-exc-c14n#' },
  { aspect: 'Digest method',    value: 'SHA-256 (all references)', uri: 'http://www.w3.org/2001/04/xmlenc#sha256' },
  { aspect: 'Signature method', value: 'RSA-SHA256 (RSA ≥2048) or ECDSA-SHA256 (P-256+)', uri: 'http://www.w3.org/2001/04/xmldsig-more#rsa-sha256' },
  { aspect: 'Signature placement', value: 'Inside <Sgntr> of a complete AppHdr (head.001.001.01) — Fr, To, BizMsgIdr, MsgDefIdr and CreDt are mandatory', uri: '' },
  { aspect: 'KeyInfo', value: 'X509Certificate only — a raw KeyValue is prohibited (key-substitution risk)', uri: '' },
];

const REFERENCES = [
  { n: 1, uri: '""', transforms: 'enveloped, exc-c14n', covers: 'The AppHdr element, excluding the entire Sgntr subtree. Only the Sgntr element is excluded — surrounding whitespace text nodes stay signed.' },
  { n: 2, uri: '(none)', transforms: 'exc-c14n', covers: 'The business Document element. The target is fixed by the scheme profile, not by generic XMLDSig processing — both sides install a profile-aware dereferencer.' },
  { n: 3, uri: '#<KeyInfoId>', transforms: 'exc-c14n', covers: 'The ds:KeyInfo element, bound by its Id — an attacker cannot swap the certificate without invalidating the signature.' },
];

const STEPS = [
  ['Load and check the credential', 'Certificate must be inside its validity window (hard failure otherwise); key strength minimums RSA 2048 / EC 256.'],
  ['Build the Business Message', 'Wrap the payload: <BusinessMessage> containing a complete <AppHdr> (Fr, To, BizMsgIdr, MsgDefIdr, CreDt, empty <Sgntr>) and the imported <Document>. Fr/To derive from the instructing and instructed agents, BizMsgIdr from GrpHdr/MsgId.'],
  ['Format before signing', 'Any readability whitespace is inserted now, so it is covered by the digests. Signed bytes are never reformatted afterwards.'],
  ['Create three References', 'AppHdr (URI=""), Document (no URI), KeyInfo (#Id) — each SHA-256, each exclusive-C14N. The dereferencer returns node-sets so the declared transforms are applied by the signature engine itself.'],
  ['Sign into AppHdr/Sgntr', 'RSA-SHA256 (or ECDSA-SHA256) over the canonicalized SignedInfo, with the "ds" prefix. KeyInfo carries the certificate only.'],
  ['Round-trip verify before sending', 'Serialize, re-parse the bytes, and run full receiver-side verification. A message that fails its own round-trip is never transmitted.'],
];

const PITFALLS = [
  ['Editing signed bytes after signing', 'Pretty-printing, entity clean-up, collapsing empty elements or line-ending fixes silently break digests. All formatting decisions happen before signing — never mutate the output.'],
  ['Inclusive instead of exclusive C14N', 'Inclusive C14N breaks signatures when the message is re-enveloped under a namespace-declaring parent (connectors, Kafka payloads, store-and-forward). The profile mandates exc-C14N everywhere.'],
  ['Emitting or trusting a KeyValue', 'A raw public key in KeyInfo enables trivial key-substitution forgery. Emit the X.509 certificate only; verifiers take the key exclusively from a certificate they can validate.'],
  ['Excluding too much around Sgntr', 'For the AppHdr reference, only the Sgntr element itself is excluded — adjacent whitespace text nodes remain part of the digested content. In lxml, remove() drops the element\'s tail text and must be compensated.'],
  ['Signing with an expired certificate', 'Both signer and verifier fail hard on a certificate outside its validity window. A cryptographically perfect signature from an invalid credential is still an invalid message.'],
];

export default function SigningGuide() {
  const [code, setCode]     = useState('');
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(REF_PATH)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then(t => { setCode(t); setLoad(false); })
      .catch(e => { setError(e.message); setLoad(false); });
  }, []);

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-primary-600" />
          Signing Guide
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          How the IIPS switch expects ISO 20022 BusinessMessages to be signed, and a reference
          implementation you can run and adapt. Getting the canonical bytes exact is the hardest
          part of integration — this page captures the profile and the gotchas.
        </p>
      </div>

      {/* Signing profile */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Signing profile</h2>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {PROFILE.map((p, i) => (
                <tr key={p.aspect} className={i % 2 ? 'bg-gray-50' : ''}>
                  <td className="px-4 py-2.5 font-medium text-gray-700 w-44 align-top">{p.aspect}</td>
                  <td className="px-4 py-2.5 text-gray-900">
                    {p.value}
                    {p.uri && <div className="font-mono text-xs text-gray-400 mt-0.5 break-all">{p.uri}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 3-reference model */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">The three references</h2>
        <p className="text-sm text-gray-600 mb-4">
          The signature digests three things. Each is canonicalized independently, so each must be
          byte-exact on its own.
        </p>

        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 mb-4">
          <svg viewBox="0 0 680 220" width="100%" role="img"
               aria-label="BusinessMessage structure showing the three signed references">
            {/* BusinessMessage wrapper */}
            <rect x="20" y="14" width="640" height="192" rx="10" fill="none" stroke="#cbd5e1" strokeWidth="1.5" />
            <text x="36" y="34" fontSize="13" fontFamily="ui-monospace, monospace" fill="#64748b">&lt;BusinessMessage&gt;</text>

            {/* AppHdr — complete BAH with Sgntr carrying the signature */}
            <rect x="44" y="46" width="592" height="92" rx="8" fill="#eff6ff" stroke="#2563eb" strokeWidth="1" />
            <text x="60" y="66" fontSize="12" fontFamily="ui-monospace, monospace" fill="#1e40af">&lt;AppHdr&gt;  Fr · To · BizMsgIdr · MsgDefIdr · CreDt</text>
            <rect x="60" y="76" width="560" height="50" rx="6" fill="#ffffff" stroke="#93c5fd" strokeWidth="1" />
            <text x="74" y="95" fontSize="12" fontFamily="ui-monospace, monospace" fill="#1e3a8a">&lt;Sgntr&gt; → &lt;ds:Signature&gt;</text>
            <text x="74" y="114" fontSize="11" fontFamily="ui-monospace, monospace" fill="#3b82f6">SignedInfo · SignatureValue · KeyInfo (Id, cert only)</text>

            {/* Document */}
            <rect x="44" y="148" width="592" height="44" rx="8" fill="#f0fdf4" stroke="#16a34a" strokeWidth="1" />
            <text x="60" y="174" fontSize="12" fontFamily="ui-monospace, monospace" fill="#166534">&lt;Document&gt;  — ISO 20022 payload (pacs.008, …)</text>

            {/* Reference badges — 1: AppHdr (minus Sgntr), 2: Document, 3: KeyInfo */}
            <circle cx="600" cy="56" r="11" fill="#2563eb" /><text x="600" y="60" fontSize="12" textAnchor="middle" fill="#fff">1</text>
            <circle cx="612" cy="170" r="11" fill="#16a34a" /><text x="612" y="174" fontSize="12" textAnchor="middle" fill="#fff">2</text>
            <circle cx="600" cy="101" r="11" fill="#2563eb" /><text x="600" y="105" fontSize="12" textAnchor="middle" fill="#fff">3</text>
          </svg>
        </div>

        <div className="space-y-2">
          {REFERENCES.map(r => (
            <div key={r.n} className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex items-center justify-center">{r.n}</span>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">URI={r.uri}</span>
                  <span className="font-mono text-xs text-gray-400">transforms: {r.transforms}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{r.covers}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <ListOrdered className="w-5 h-5 text-gray-400" /> Signing process
        </h2>
        <ol className="space-y-2">
          {STEPS.map(([title, desc], i) => (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-gray-900 text-white text-xs font-semibold flex items-center justify-center">{i + 1}</span>
              <div>
                <p className="text-sm font-medium text-gray-900">{title}</p>
                <p className="text-sm text-gray-600">{desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Pitfalls */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" /> Common pitfalls
        </h2>
        <p className="text-sm text-gray-600 mb-3">
          These are the byte-exactness traps that make a signature verify locally but fail at the switch.
        </p>
        <div className="space-y-2">
          {PITFALLS.map(([title, desc]) => (
            <div key={title} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm font-medium text-amber-900">{title}</p>
              <p className="text-sm text-amber-800 mt-0.5">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Reference implementation */}
      <section>
        <div className="flex items-center justify-between mb-3 gap-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileCode2 className="w-5 h-5 text-gray-400" /> Reference implementation
          </h2>
          <div className="flex items-center gap-2">
            <button onClick={copyCode} disabled={!code}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <a href={REF_PATH} download
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700">
              <Download className="w-4 h-4" /> Download .java
            </a>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-3">
          <span className="font-mono text-xs">Iso20022BahMessageSigner.java</span> — a self-contained
          CLI signer using only the JDK's built-in JSR-105 API (no external dependencies, JDK 17+).
          It builds the complete BAH, signs into <span className="font-mono text-xs">AppHdr/Sgntr</span>,
          and round-trip verifies before writing the signed Business Message. Use it as a spec to
          mirror in your own stack. The standalone conformance verifier,{' '}
          <a href={VERIFIER_PATH} download className="font-mono text-xs text-primary-600 hover:underline">
            Iso20022BahMessageVerifier.java
          </a>, runs the same five-stage pipeline the switch applies to inbound messages.
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading reference source…
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            Could not load reference source: {error}
          </div>
        )}
        {!loading && !error && (
          <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-auto max-h-[480px] leading-relaxed">
            {code}
          </pre>
        )}

        {/* Usage */}
        <h3 className="text-base font-medium text-gray-900 mt-5 mb-2 flex items-center gap-2">
          <Terminal className="w-4 h-4 text-gray-400" /> Usage
        </h3>
        <div className="space-y-2">
          <div>
            <p className="text-xs text-gray-500 mb-1">Compile it once (JDK 17 or later; no other dependencies):</p>
            <pre className="bg-gray-100 text-gray-800 text-xs rounded-lg p-3 overflow-x-auto">javac Iso20022BahMessageSigner.java</pre>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Mode A — PEM key (unencrypted PKCS#8):</p>
            <pre className="bg-gray-100 text-gray-800 text-xs rounded-lg p-3 overflow-x-auto">java Iso20022BahMessageSigner sign --document pacs008.xml --cert leaf.pem --key key-pkcs8.pem</pre>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Mode B — PKCS#12 keystore:</p>
            <pre className="bg-gray-100 text-gray-800 text-xs rounded-lg p-3 overflow-x-auto">java Iso20022BahMessageSigner sign --document pacs008.xml --cert leaf.pem --p12 ks.p12 --alias a --pass p</pre>
          </div>
        </div>
      </section>
    </div>
  );
}
